import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Copy, RefreshCw, XCircle, Clock, CheckCircle, Phone, Search, MessageCircle, X } from 'react-feather';
import { virtualNumbersAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { formatCurrencyWithConversion } from '../utils/currency';
import './VirtualNumbers.css';

// ── Helpers ──────────────────────────────────────────────────────────────────

const SERVICE_ICONS = {
  whatsapp:   { emoji: '💬', color: '#25D366' },
  telegram:   { emoji: '✈️', color: '#0088cc' },
  google:     { emoji: '🔍', color: '#DB4437' },
  facebook:   { emoji: '👤', color: '#1877F2' },
  instagram:  { emoji: '📸', color: '#E4405F' },
  tiktok:     { emoji: '🎵', color: '#010101' },
  twitter:    { emoji: '🐦', color: '#1DA1F2' },
  discord:    { emoji: '🎮', color: '#5865F2' },
  snapchat:   { emoji: '👻', color: '#FFFC00' },
  netflix:    { emoji: '🎬', color: '#E50914' },
  microsoft:  { emoji: '🪟', color: '#00A4EF' },
  apple:      { emoji: '🍎', color: '#555' },
  amazon:     { emoji: '📦', color: '#FF9900' },
  uber:       { emoji: '🚗', color: '#000' },
  spotify:    { emoji: '🎧', color: '#1DB954' },
  linkedin:   { emoji: '💼', color: '#0A66C2' },
  tinder:     { emoji: '🔥', color: '#FE3C72' },
  paypal:     { emoji: '💳', color: '#003087' },
};

function getServiceStyle(name = '') {
  const key = name.toLowerCase();
  for (const [k, v] of Object.entries(SERVICE_ICONS)) {
    if (key.includes(k)) return v;
  }
  return { emoji: '📱', color: '#6366F1' };
}

const FLAG_MAP = {
  'argentina':'🇦🇷','australia':'🇦🇺','brazil':'🇧🇷','cameroon':'🇨🇲',
  'united states':'🇺🇸','usa':'🇺🇸','united kingdom':'🇬🇧','uk':'🇬🇧',
  'canada':'🇨🇦','france':'🇫🇷','germany':'🇩🇪','india':'🇮🇳',
  'russia':'🇷🇺','china':'🇨🇳','japan':'🇯🇵','spain':'🇪🇸',
  'italy':'🇮🇹','mexico':'🇲🇽','indonesia':'🇮🇩','turkey':'🇹🇷',
  'vietnam':'🇻🇳','thailand':'🇹🇭','philippines':'🇵🇭','egypt':'🇪🇬',
  'south africa':'🇿🇦','nigeria':'🇳🇬','colombia':'🇨🇴','poland':'🇵🇱',
  'ukraine':'🇺🇦','malaysia':'🇲🇾','kenya':'🇰🇪','ghana':'🇬🇭',
  'morocco':'🇲🇦','pakistan':'🇵🇰','bangladesh':'🇧🇩','sri lanka':'🇱🇰',
};
function getFlag(name = '') { return FLAG_MAP[name.toLowerCase()] || '🌍'; }

// ── Component ─────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'services',   label: 'Services' },
  { id: 'countries',  label: 'Countries' },
  { id: 'rent',       label: 'Rent Number' },
  { id: 'orders',     label: 'Orders' },
  { id: 'codes',      label: 'Codes' },
];

export default function VirtualNumbers() {
  const { user, refreshUser } = useAuth();

  const [tab, setTab]                 = useState('services');
  const [search, setSearch]           = useState('');

  // Data
  const [services, setServices]       = useState([]);
  const [countries, setCountries]     = useState([]);
  const [orders, setOrders]           = useState([]);
  const [prices, setPrices]           = useState([]);

  // Selection
  const [selectedService, setSelectedService] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedPrice,   setSelectedPrice]   = useState(null);

  // UI state
  const [loading, setLoading]         = useState(true);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [purchasing, setPurchasing]   = useState(false);
  const [checking,   setChecking]     = useState(null);   // orderId being checked
  const [canceling,  setCanceling]    = useState(null);   // orderId being cancelled
  const [copied,     setCopied]       = useState(null);
  const [msg, setMsg]                 = useState({ text: '', type: '' });
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);

  const resetPurchaseFlow = useCallback(() => {
    setSelectedService(null);
    setSelectedCountry(null);
    setSelectedPrice(null);
    setPrices([]);
    setShowPurchaseModal(false);
    setSearch('');
  }, []);

  // ── Data fetching ─────────────────────────────────────────────────────────

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [svcRes, cntRes, ordRes] = await Promise.all([
        virtualNumbersAPI.getServices(),
        virtualNumbersAPI.getCountries(),
        virtualNumbersAPI.getOrders(),
      ]);
      setServices(svcRes.data.services  || []);
      setCountries(cntRes.data.countries || []);
      setOrders(ordRes.data.orders      || []);
    } catch (e) {
      setMsg({ text: 'Failed to load data. Check your API connection.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Auto-refresh orders every 10 s when on Orders or Codes tab
  useEffect(() => {
    if (tab !== 'orders' && tab !== 'codes') return;
    const id = setInterval(async () => {
      try {
        const res = await virtualNumbersAPI.getOrders();
        setOrders(res.data.orders || []);
      } catch {}
    }, 10000);
    return () => clearInterval(id);
  }, [tab]);

  // ── Actions ───────────────────────────────────────────────────────────────

  const fetchPrices = async (serviceCode) => {
    setLoadingPrices(true);
    setPrices([]);
    try {
      const res = await virtualNumbersAPI.getPrices(serviceCode);
      setPrices(res.data.prices || []);
    } catch {
      setMsg({ text: 'Failed to fetch prices for this service', type: 'error' });
    } finally {
      setLoadingPrices(false);
    }
  };

  const handleSelectService = (svc) => {
    setSelectedService(svc);
    setSelectedCountry(null);
    setSelectedPrice(null);
    setSearch('');
    fetchPrices(svc.code);
  };

  const handleSelectCountry = async (country) => {
    setSelectedCountry(country);
    setSelectedService(null);
    setSelectedPrice(null);
    setSearch('');
    setLoadingPrices(true);
    setPrices([]);
    // Load all services then filter by country in prices
    // We'll just show a service picker for the country
    setLoadingPrices(false);
  };

  const handleSelectPriceRow = (priceItem) => {
    setSelectedPrice(priceItem);
    setShowPurchaseModal(true);
  };

  const handlePurchase = async () => {
    if (!selectedService || !selectedPrice) return;
    setPurchasing(true);
    setMsg({ text: '', type: '' });
    try {
      await virtualNumbersAPI.purchaseNumber(
        selectedPrice.countryId,
        selectedService.code,
        selectedPrice.countryName,
        selectedService.name
      );
      setMsg({ text: '✅ Number purchased! Check Orders tab.', type: 'success' });
      setShowPurchaseModal(false);
      setSelectedPrice(null);
      setSelectedService(null);
      setSelectedCountry(null);
      setPrices([]);
      await refreshUser();
      const res = await virtualNumbersAPI.getOrders();
      setOrders(res.data.orders || []);
      setTab('orders');
    } catch (e) {
      setMsg({ text: e.response?.data?.error || 'Purchase failed', type: 'error' });
    } finally {
      setPurchasing(false);
    }
  };

  const handleCheckSMS = async (orderId) => {
    setChecking(orderId);
    try {
      await virtualNumbersAPI.checkOrder(orderId);
      const res = await virtualNumbersAPI.getOrders();
      setOrders(res.data.orders || []);
    } catch {}
    setChecking(null);
  };

  const handleCancel = async (orderId) => {
    setCanceling(orderId);
    try {
      await virtualNumbersAPI.cancelOrder(orderId);
      await refreshUser();
      const res = await virtualNumbersAPI.getOrders();
      setOrders(res.data.orders || []);
      setMsg({ text: 'Order cancelled and refunded.', type: 'success' });
    } catch (e) {
      setMsg({ text: e.response?.data?.error || 'Cancel failed', type: 'error' });
    }
    setCanceling(null);
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(text);
    setTimeout(() => setCopied(null), 2000);
  };

  const switchTab = (id) => {
    setTab(id);
    setSearch('');
    if (id !== 'services' && id !== 'countries') {
      resetPurchaseFlow();
    }
  };

  // ── Filtered lists ────────────────────────────────────────────────────────

  const filteredServices = useMemo(() =>
    services.filter(s => s.name.toLowerCase().includes(search.toLowerCase())),
    [services, search]);

  const filteredCountries = useMemo(() =>
    countries.filter(c => c.name.toLowerCase().includes(search.toLowerCase())),
    [countries, search]);

  const filteredRentPrices = useMemo(() =>
    prices.filter(p => p.countryName.toLowerCase().includes(search.toLowerCase())),
    [prices, search]);

  const filteredRentServices = useMemo(() =>
    services.filter(s => s.name.toLowerCase().includes(search.toLowerCase())),
    [services, search]);

  const activeOrders    = orders.filter(o => o.status === 'pending');
  const completedOrders = orders.filter(o => o.status === 'completed');

  // ── Render ────────────────────────────────────────────────────────────────

  const renderServices = () => {
    if (loading) return <div className="vn-center"><div className="spinner" /></div>;

    if (selectedService) {
      return (
        <div>
          <button
            className="vn-back-btn"
            onClick={() => {
              setSelectedService(null);
              setSelectedPrice(null);
              setPrices([]);
              setShowPurchaseModal(false);
              setSearch('');
            }}
          >
            ‹ Back to Services
          </button>
          <div className="vn-rent-service-header">
            <div className="vn-row-icon" style={{ background: getServiceStyle(selectedService.name).color + '20', width: 48, height: 48, borderRadius: 14, fontSize: 24 }}>
              {getServiceStyle(selectedService.name).emoji}
            </div>
            <div>
              <div className="vn-rent-title">{selectedService.name}</div>
              <div className="vn-rent-sub">Select a country to continue your purchase</div>
            </div>
          </div>

          <div className="vn-search vn-rent-search">
            <Search size={16} color="var(--text-muted)" className="vn-search-icon" />
            <input
              type="text"
              placeholder="Search countries..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {loadingPrices ? (
            <div className="vn-center"><div className="spinner" /></div>
          ) : filteredRentPrices.length === 0 ? (
            <div className="vn-empty">No countries available for this service right now.</div>
          ) : (
            <div className="vn-price-list">
              {filteredRentPrices.map((p, i) => (
                <button
                  key={`${p.countryId}-${i}`}
                  className={`vn-price-row ${selectedPrice?.countryId === p.countryId ? 'selected' : ''}`}
                  onClick={() => handleSelectPriceRow(p)}
                >
                  <div className="vn-price-left">
                    <span className="vn-price-flag">{getFlag(p.countryName)}</span>
                    <div>
                      <div className="vn-price-country">{p.countryName}</div>
                      <div className="vn-price-rate">Success Rate: {p.successRate}%</div>
                    </div>
                  </div>
                  <div className="vn-price-right">
                    <span className="vn-price-amount">{formatCurrencyWithConversion(p.price, user?.currency, 2)}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (!filteredServices.length) return <div className="vn-empty">No services found.</div>;
    return (
      <div className="vn-list">
        {filteredServices.map(svc => {
          const style = getServiceStyle(svc.name);
          return (
            <button key={svc.code} className="vn-row" onClick={() => handleSelectService(svc)}>
              <div className="vn-row-icon" style={{ background: style.color + '20' }}>
                <span>{style.emoji}</span>
              </div>
              <span className="vn-row-name">{svc.name}</span>
              <span className="vn-row-chevron">›</span>
            </button>
          );
        })}
      </div>
    );
  };

  const renderCountries = () => {
    if (loading) return <div className="vn-center"><div className="spinner" /></div>;

    if (selectedCountry) {
      return (
        <div>
          <button
            className="vn-back-btn"
            onClick={() => {
              setSelectedCountry(null);
              setSelectedService(null);
              setSelectedPrice(null);
              setShowPurchaseModal(false);
              setSearch('');
            }}
          >
            ‹ Back to Countries
          </button>
          <div className="vn-rent-service-header">
            <span style={{ fontSize: 40 }}>{getFlag(selectedCountry.name)}</span>
            <div>
              <div className="vn-rent-title">{selectedCountry.name}</div>
              <div className="vn-rent-sub">Select a service to continue your purchase</div>
            </div>
          </div>

          <div className="vn-search vn-rent-search">
            <Search size={16} color="var(--text-muted)" className="vn-search-icon" />
            <input
              type="text"
              placeholder="Search services..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          <div className="vn-list">
            {filteredRentServices.map(svc => {
              const style = getServiceStyle(svc.name);
              return (
                <button key={svc.code} className="vn-row" onClick={async () => {
                  setSelectedService(svc);
                  setLoadingPrices(true);
                  setPrices([]);
                  try {
                    const res = await virtualNumbersAPI.getPrices(svc.code);
                    const allPrices = res.data.prices || [];
                    const match = allPrices.find(p =>
                      String(p.countryId) === String(selectedCountry.id) ||
                      p.countryName.toLowerCase() === selectedCountry.name.toLowerCase()
                    );
                    if (match) {
                      setPrices(allPrices);
                      setSelectedPrice({ ...match, countryId: selectedCountry.id, countryName: selectedCountry.name });
                      setShowPurchaseModal(true);
                    } else {
                      setMsg({ text: `${svc.name} is not available in ${selectedCountry.name}.`, type: 'error' });
                      setSelectedService(null);
                    }
                  } catch {
                    setMsg({ text: 'Failed to check service availability', type: 'error' });
                  } finally {
                    setLoadingPrices(false);
                  }
                }}>
                  <div className="vn-row-icon" style={{ background: style.color + '20' }}>{style.emoji}</div>
                  <span className="vn-row-name">{svc.name}</span>
                  <span className="vn-row-chevron">›</span>
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    if (!filteredCountries.length) return <div className="vn-empty">No countries found.</div>;
    return (
      <div className="vn-list">
        {filteredCountries.map(c => (
          <button key={c.id} className="vn-row" onClick={() => handleSelectCountry(c)}>
            <div className="vn-row-icon vn-flag-icon">
              <span style={{ fontSize: 22 }}>{getFlag(c.name)}</span>
            </div>
            <div className="vn-row-info">
              <span className="vn-row-name">{c.name}</span>
              <span className="vn-row-sub">Tap to see services</span>
            </div>
            <span className="vn-row-chevron">›</span>
          </button>
        ))}
      </div>
    );
  };

  const renderRent = () => {
    return (
      <div className="vn-rent-empty">
        <Clock size={48} color="var(--text-muted)" />
        <p>Rent Numbers comming soon!</p>
      </div>
    );
  };

  const renderPurchaseModal = () => {
    if (!showPurchaseModal || !selectedService || !selectedPrice) return null;

    return (
      <div className="vn-modal-backdrop" onClick={() => setShowPurchaseModal(false)}>
        <div className="vn-modal-card" onClick={e => e.stopPropagation()}>
          <div className="vn-modal-head">
            <div className="vn-modal-handle" />
            <div className="vn-modal-head-row">
              <div>
                <div className="vn-modal-title">Confirm Purchase</div>
              </div>
              <button className="vn-modal-close" onClick={() => setShowPurchaseModal(false)}>
                <X size={18} />
              </button>
            </div>
          </div>

          <div className="vn-modal-summary">
            <div className="vn-modal-summary-icon">
              <MessageCircle size={18} />
            </div>
            <div className="vn-modal-summary-copy">
              <strong>{selectedService.name}</strong>
              <span>Virtual SMS Number</span>
            </div>
          </div>

          <div className="vn-modal-details">
            <div className="vn-modal-detail">
              <span>Service</span>
              <strong>{getServiceStyle(selectedService.name).emoji} {selectedService.name}</strong>
            </div>
            <div className="vn-modal-detail">
              <span>Country</span>
              <strong>{getFlag(selectedPrice.countryName)} {selectedPrice.countryName}</strong>
            </div>
            <div className="vn-modal-detail">
              <span>Success Rate</span>
              <strong>{selectedPrice.successRate}%</strong>
            </div>
            <div className="vn-modal-detail">
              <span>Price</span>
              <strong>{formatCurrencyWithConversion(selectedPrice.price, user?.currency, 2)}</strong>
            </div>
          </div>

          <p className="vn-modal-note">
            A number will be assigned to you instantly. You have a limited time to receive your SMS code.
          </p>

          <button className="btn btn-primary vn-modal-action" onClick={handlePurchase} disabled={purchasing}>
            {purchasing ? 'Purchasing…' : 'Purchase Number'}
          </button>
        </div>
      </div>
    );
  };

  const renderOrders = () => {
    if (loading) return <div className="vn-center"><div className="spinner" /></div>;
    if (activeOrders.length === 0) return (
      <div className="vn-empty">
        <Clock size={40} color="var(--text-muted)" />
        <p>No active numbers. <br />Rent a number from the <strong>Services</strong> tab.</p>
      </div>
    );
    return (
      <div className="vn-order-list">
        {activeOrders.map(order => {
          const style = getServiceStyle(order.service_name);
          return (
            <div key={order.id} className="vn-order-card">
              <div className="vn-order-top">
                <div className="vn-order-svc">
                  <span className="vn-order-icon" style={{ background: style.color + '20' }}>{style.emoji}</span>
                  <div>
                    <div className="vn-order-name">{order.service_name}</div>
                    <div className="vn-order-country">{getFlag(order.country_name)} {order.country_name}</div>
                  </div>
                </div>
                <span className="badge badge-pending">PENDING</span>
              </div>
              <div className="vn-order-number-box">
                <Phone size={16} color="var(--primary)" />
                <span className="vn-phone">+{order.phone_number}</span>
                <button className="vn-copy-btn" onClick={() => handleCopy(order.phone_number)}>
                  {copied === order.phone_number ? <CheckCircle size={14} color="var(--green-500)" /> : <Copy size={14} />}
                </button>
              </div>
              <div className="vn-order-sms-status">
                <Clock size={14} className="spin-slow" />
                <span>Waiting for SMS code…</span>
              </div>
              <div className="vn-order-actions">
                <button className="btn btn-secondary btn-sm" onClick={() => handleCheckSMS(order.order_id)} disabled={checking === order.order_id}>
                  <RefreshCw size={13} /> {checking === order.order_id ? 'Checking…' : 'Check SMS'}
                </button>
                <button className="btn btn-sm vn-cancel-btn" onClick={() => handleCancel(order.order_id)} disabled={canceling === order.order_id}>
                  <XCircle size={13} /> {canceling === order.order_id ? 'Cancelling…' : 'Cancel'}
                </button>
              </div>
              <div className="vn-order-cost">Cost: {formatCurrencyWithConversion(order.cost, user?.currency, 2)} · {new Date(order.created_at).toLocaleString()}</div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderCodes = () => {
    if (loading) return <div className="vn-center"><div className="spinner" /></div>;
    if (completedOrders.length === 0) return (
      <div className="vn-empty">
        <CheckCircle size={40} color="var(--text-muted)" />
        <p>No SMS codes yet. Codes will appear here once received.</p>
      </div>
    );
    return (
      <div className="vn-order-list">
        {completedOrders.map(order => {
          const style = getServiceStyle(order.service_name);
          return (
            <div key={order.id} className="vn-order-card vn-order-done">
              <div className="vn-order-top">
                <div className="vn-order-svc">
                  <span className="vn-order-icon" style={{ background: style.color + '20' }}>{style.emoji}</span>
                  <div>
                    <div className="vn-order-name">{order.service_name}</div>
                    <div className="vn-order-country">{getFlag(order.country_name)} {order.country_name}</div>
                  </div>
                </div>
                <span className="badge badge-active">DONE</span>
              </div>
              <div className="vn-order-number-box">
                <Phone size={16} color="var(--primary)" />
                <span className="vn-phone">+{order.phone_number}</span>
              </div>
              <div className="vn-code-box">
                <div>
                  <div className="vn-code-label">SMS Code</div>
                  <div className="vn-code-value">{order.sms_code}</div>
                  {order.sms_text && <div className="vn-code-text">{order.sms_text}</div>}
                </div>
                <button className="vn-copy-btn vn-copy-large" onClick={() => handleCopy(order.sms_code)}>
                  {copied === order.sms_code ? <CheckCircle size={16} color="var(--green-500)" /> : <Copy size={16} />}
                  {copied === order.sms_code ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <div className="vn-order-cost">Cost: {formatCurrencyWithConversion(order.cost, user?.currency, 2)} · {new Date(order.created_at).toLocaleString()}</div>
            </div>
          );
        })}
      </div>
    );
  };

  // ── Main render ───────────────────────────────────────────────────────────

  return (
    <div className="vn-page">
      <h1 className="page-title vn-page-title">Virtual Numbers</h1>
      <p className="page-subtitle">Rent temporary numbers for SMS verification</p>

      {msg.text && (
        <div className={`alert alert-${msg.type}`} style={{ marginBottom: 16 }} onClick={() => setMsg({ text: '', type: '' })}>
          {msg.text}
        </div>
      )}

      <div className="card vn-card">
        {/* ── Search bar ── */}
        {(tab === 'services' || tab === 'countries') && (
          <div className="vn-search">
            <Search size={16} color="var(--text-muted)" className="vn-search-icon" />
            <input
              type="text"
              placeholder={tab === 'countries' ? 'Search countries…' : 'Search services…'}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        )}

        {/* ── Tab bar ── */}
        <div className="vn-tab-bar">
          {TABS.map(t => (
            <button
              key={t.id}
              className={`vn-tab-btn ${tab === t.id ? 'active' : ''}`}
              onClick={() => switchTab(t.id)}
            >
              {t.label}
              {t.id === 'orders' && activeOrders.length > 0 && (
                <span className="vn-tab-badge">{activeOrders.length}</span>
              )}
              {t.id === 'codes' && completedOrders.length > 0 && (
                <span className="vn-tab-badge vn-tab-badge-green">{completedOrders.length}</span>
              )}
            </button>
          ))}
        </div>

        {/* ── Tab content ── */}
        <div className="vn-content">
          {tab === 'services'  && renderServices()}
          {tab === 'countries' && renderCountries()}
          {tab === 'rent'      && renderRent()}
          {tab === 'orders'    && renderOrders()}
          {tab === 'codes'     && renderCodes()}
        </div>
      </div>
      {renderPurchaseModal()}
    </div>
  );
}
